import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Popconfirm, Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import { Queue } from '@totem/components/ticketing/queueManagement/types';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { QUEUES_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

import '../../ticketing.css';

type QueueFormValues = {
  name: string;
};

const activeAssigneeCount = (queue: Queue): number =>
  queue.assignees.filter((assignee) => !assignee.endTime).length;

const QueueManagementListPage = () => {
  const { getApiToken } = useContext(UserProfileContext);
  const [queues, setQueues] = useState<Queue[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm<QueueFormValues>();

  const fetchQueues = useCallback(() => {
    setLoading(true);

    fetch(`${QUEUES_ENDPOINT}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (res.status >= 400) {
          return null;
        }
        return res.json();
      })
      .then((result: Queue[]) => {
        setQueues(isNotNull(result) ? result : []);
      })
      .finally(() => setLoading(false));
  }, [getApiToken]);

  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  const handleCreateQueue = async () => {
    try {
      const values = await form.validateFields();
      setIsSaving(true);

      fetch(`${QUEUES_ENDPOINT}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify({ name: values.name }),
      }).then((res) => {
        setIsSaving(false);
        const success = CheckResponseShowError(res);
        if (success) {
          setShowCreateModal(false);

          form.resetFields();

          fetchQueues();
        }
      });
    } catch {
      setIsSaving(false);
    }
  };

  const handleDeleteQueue = (queue: Queue) => {
    fetch(`${QUEUES_ENDPOINT}/${queue.id}`, {
      method: 'DELETE',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    }).then((res) => {
      const success = CheckResponseShowError(res);
      if (success) {
        fetchQueues();
      }
    });
  };

  const columns: ColumnProps<Queue>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (rec, cmp) => (rec.name || '').localeCompare(cmp.name || ''),
      render: (_, queue: Queue) => (
        <Link to={`/dashboard/ticketingQueueManagement/${queue.id}`}>
          {queue.name}
        </Link>
      ),
    },
    {
      title: 'Assignees',
      key: 'assignees',
      sorter: (rec, cmp) => activeAssigneeCount(rec) - activeAssigneeCount(cmp),
      render: (_, queue: Queue) => activeAssigneeCount(queue),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, queue: Queue) => (
        <div className="center-table-cell">
          <Tooltip title="Edit Queue">
            <Link to={`/dashboard/ticketingQueueManagement/${queue.id}`}>
              <Button icon={<EditOutlined />} shape="circle" />
            </Link>
          </Tooltip>
          &nbsp;&nbsp;
          <Tooltip title="Delete Queue">
            <Popconfirm
              title="Delete this queue?"
              description={`Remove "${queue.name}"? Any tickets currently assigned to this queue will be unassigned from it. This cannot be undone.`}
              okText="Delete"
              okType="danger"
              cancelText="Cancel"
              onConfirm={() => handleDeleteQueue(queue)}
            >
              <Button danger icon={<DeleteOutlined />} shape="circle" />
            </Popconfirm>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <ContentLayout>
      <IBCard
        isCovered
        title="Queue Administration"
        extra={
          <>
            <RecordCountBadge count={queues.length} />
            &nbsp;&nbsp;&nbsp;
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowCreateModal(true)}
            >
              New Queue
            </Button>
          </>
        }
      >
        <IBTable<Queue>
          showSorterTooltip
          columns={columns}
          dataSource={queues}
          loading={loading}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
          }}
          rowKey={(record) => record.id}
        />
      </IBCard>

      <Modal
        title="Create Queue"
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onOk={handleCreateQueue}
        okText="Create"
        confirmLoading={isSaving}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Name is required.' }]}
          >
            <Input placeholder="Name" />
          </Form.Item>
        </Form>
      </Modal>
    </ContentLayout>
  );
};

export default QueueManagementListPage;
